import type { IFramePostMessageData } from '../types';
import { IFrameLoader } from './iframe-loader';
import { IFrameMessageHandler } from './iframe-message-handler';
import { IFrameUpdater } from './iframe-updater';
import { IFrameUrlState } from './iframe-url-state';

export interface JJMCIFrameOptions {
  id: string;
  origin: string;
  startingPathname: string;
  onMessage: (message: IFramePostMessageData) => void;
}

export class JJMCIFrame {
  private readonly listeners = new Map<string, EventListener>();

  private iframe?: HTMLIFrameElement;
  private updater?: IFrameUpdater;
  private status: 'idle' | 'initialized' | 'loading' | 'failed' | 'ready' = 'idle';

  constructor(
    readonly options: JJMCIFrameOptions,
    private readonly messageHandler: IFrameMessageHandler,
    private readonly iframeLoader: IFrameLoader = new IFrameLoader(options.id, { timeout: 10000 }),
    private readonly urlState: IFrameUrlState = new IFrameUrlState(
      options.origin,
      options.startingPathname
    )
  ) {
    this.messageHandler = new IFrameMessageHandler(options.origin, options.onMessage);
    this.urlState = new IFrameUrlState(options.origin, options.startingPathname);
  }

  init() {
    if (this.status !== 'idle') return this;

    this.messageHandler.register('height', (value) => this.updater?.setHeight(value));
    this.messageHandler.register('location', (value) => this.updater?.setLocation(value));

    this.addListener('message', (e: MessageEvent) => this.messageHandler.handle(e));

    this.status = 'initialized';

    if (document.readyState === 'loading') {
      this.addListener('DOMContentLoaded', () => this.load());
    } else {
      this.load();
    }

    return this;
  }

  dispose(): void {
    this.listeners.forEach((listener, eventName) => {
      window.removeEventListener(eventName, listener, false);
    });
    this.listeners.clear();
    this.status = 'idle';
  }

  getStatus() {
    return this.status;
  }

  private addListener(eventName: string, listener: EventListener): void {
    if (this.listeners.has(eventName)) return;

    window.addEventListener(eventName, listener, false);
    this.listeners.set(eventName, listener);
  }

  private async load(): Promise<void> {
    this.status = 'loading';
    this.setInitialLocation();

    try {
      this.iframe = await this.iframeLoader.load();
    } catch {
      this.status = 'failed';
      return;
    }

    this.updater = new IFrameUpdater(this.iframe, this.urlState);

    this.iframe.src = this.urlState.href;
    this.status = 'ready';
  }

  private setInitialLocation() {
    const initialLocation = this.extractReqLocation();

    if (initialLocation) {
      this.urlState.setLocation(initialLocation);
    }
  }

  private extractReqLocation() {
    const req = new URL(window.location.href).searchParams.get('req');

    if (!req) return null;

    const [pathname, search = ''] = decodeURIComponent(req).split('?');

    return { pathname, search };
  }
}
